<template>
  <div class="image-viewer" @wheel="handleWheel" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp" @mouseleave="handleMouseUp" @touchstart="handleTouchStart" @touchmove="handleTouchMove" @touchend="handleTouchEnd">
    <img
      ref="image"
      :src="src"
      :style="{
        transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
        transformOrigin: 'top left',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        maxWidth: 'none',
        maxHeight: 'none'
      }"
      @load="handleImageLoad"
      @dragstart.prevent
    />
    
    <!-- Zoom Controls -->
    <div class="zoom-controls">
      <button @click="zoomIn" class="zoom-button" title="Zoom In">
        <i class="las la-plus"></i>
      </button>
      <button @click="zoomOut" class="zoom-button" title="Zoom Out">
        <i class="las la-minus"></i>
      </button>
      <button @click="resetZoom" class="zoom-button" title="Reset Zoom">
        <i class="las la-home"></i>
      </button>
    </div>
    
    <!-- Zoom Percentage Display -->
    <div class="zoom-percentage">
      {{ Math.round(scale * 100) }}%
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageViewer',
  props: {
    src: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      imageWidth: 0,
      imageHeight: 0,
      containerWidth: 0,
      containerHeight: 0,
      minScale: 0.1,
      maxScale: 5
    }
  },
  mounted() {
    this.updateContainerSize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    updateContainerSize() {
      if (this.$el) {
        this.containerWidth = this.$el.clientWidth
        this.containerHeight = this.$el.clientHeight
      }
    },
    handleResize() {
      this.updateContainerSize()
      if (this.imageWidth && this.imageHeight) {
        this.fitImage()
      }
    },
    handleImageLoad() {
      const img = this.$refs.image
      this.imageWidth = img.naturalWidth
      this.imageHeight = img.naturalHeight
      this.updateContainerSize()
      this.fitImage()
    },
    fitImage() {
      if (!this.imageWidth || !this.imageHeight || !this.containerWidth || !this.containerHeight) {
        return
      }

      // Add small border (10px on each side)
      const border = 20
      const availableWidth = this.containerWidth - border
      const availableHeight = this.containerHeight - border

      const scaleX = availableWidth / this.imageWidth
      const scaleY = availableHeight / this.imageHeight
      
      // Fill the viewer more (remove the Math.min with 1 constraint)
      // Only keep image small if it's naturally smaller than the viewer
      this.scale = Math.min(scaleX, scaleY)
      this.minScale = Math.min(scaleX, scaleY) * 0.5

      this.centerImage()
    },
    centerImage() {
      const scaledWidth = this.imageWidth * this.scale
      const scaledHeight = this.imageHeight * this.scale

      this.translateX = (this.containerWidth - scaledWidth) / 2 / this.scale
      this.translateY = (this.containerHeight - scaledHeight) / 2 / this.scale
    },
    handleWheel(e) {
      e.preventDefault()

      const rect = this.$el.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      const imageMouseX = (mouseX - this.translateX * this.scale) / this.scale
      const imageMouseY = (mouseY - this.translateY * this.scale) / this.scale

      const delta = e.deltaY > 0 ? 0.9 : 1.1
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * delta))

      if (newScale !== this.scale) {
        this.translateX = mouseX / newScale - imageMouseX
        this.translateY = mouseY / newScale - imageMouseY
        this.scale = newScale
        this.constrainTranslation()
      }
    },
    constrainTranslation() {
      const scaledWidth = this.imageWidth * this.scale
      const scaledHeight = this.imageHeight * this.scale

      // Allow some background to show - add margin (50px on each side when zoomed)
      const margin = 50
      const marginInImageSpace = margin / this.scale

      const maxTranslateX = marginInImageSpace
      const minTranslateX = (this.containerWidth - scaledWidth) / this.scale - marginInImageSpace
      const maxTranslateY = marginInImageSpace
      const minTranslateY = (this.containerHeight - scaledHeight) / this.scale - marginInImageSpace

      if (scaledWidth <= this.containerWidth) {
        this.translateX = (this.containerWidth - scaledWidth) / 2 / this.scale
      } else {
        this.translateX = Math.max(minTranslateX, Math.min(maxTranslateX, this.translateX))
      }

      if (scaledHeight <= this.containerHeight) {
        this.translateY = (this.containerHeight - scaledHeight) / 2 / this.scale
      } else {
        this.translateY = Math.max(minTranslateY, Math.min(maxTranslateY, this.translateY))
      }
    },
    handleMouseDown(e) {
      e.preventDefault()
      this.isDragging = true
      this.lastMouseX = e.clientX
      this.lastMouseY = e.clientY
    },
    handleMouseMove(e) {
      if (!this.isDragging) return

      e.preventDefault()
      const deltaX = (e.clientX - this.lastMouseX) / this.scale
      const deltaY = (e.clientY - this.lastMouseY) / this.scale

      this.translateX += deltaX
      this.translateY += deltaY

      this.lastMouseX = e.clientX
      this.lastMouseY = e.clientY

      this.constrainTranslation()
    },
    handleMouseUp() {
      this.isDragging = false
    },
    handleTouchStart(e) {
      e.preventDefault()
      if (e.touches.length === 1) {
        this.isDragging = true
        this.lastMouseX = e.touches[0].clientX
        this.lastMouseY = e.touches[0].clientY
      } else if (e.touches.length === 2) {
        this.isDragging = false
        this.handlePinchStart(e)
      }
    },
    handleTouchMove(e) {
      e.preventDefault()
      if (e.touches.length === 1 && this.isDragging) {
        const deltaX = (e.touches[0].clientX - this.lastMouseX) / this.scale
        const deltaY = (e.touches[0].clientY - this.lastMouseY) / this.scale

        this.translateX += deltaX
        this.translateY += deltaY

        this.lastMouseX = e.touches[0].clientX
        this.lastMouseY = e.touches[0].clientY

        this.constrainTranslation()
      } else if (e.touches.length === 2) {
        this.handlePinchMove(e)
      }
    },
    handleTouchEnd(e) {
      if (e.touches.length === 0) {
        this.isDragging = false
      }
    },
    handlePinchStart(e) {
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      this.initialDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      )
      this.initialScale = this.scale

      const rect = this.$el.getBoundingClientRect()
      this.pinchCenterX = (touch1.clientX + touch2.clientX) / 2 - rect.left
      this.pinchCenterY = (touch1.clientY + touch2.clientY) / 2 - rect.top
    },
    handlePinchMove(e) {
      const touch1 = e.touches[0]
      const touch2 = e.touches[1]
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      )

      if (this.initialDistance > 0) {
        const scaleFactor = distance / this.initialDistance
        const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.initialScale * scaleFactor))

        const imageMouseX = (this.pinchCenterX - this.translateX * this.scale) / this.scale
        const imageMouseY = (this.pinchCenterY - this.translateY * this.scale) / this.scale

        this.translateX = this.pinchCenterX / newScale - imageMouseX
        this.translateY = this.pinchCenterY / newScale - imageMouseY
        this.scale = newScale

        this.constrainTranslation()
      }
    },
    zoomIn() {
      const centerX = this.containerWidth / 2
      const centerY = this.containerHeight / 2
      
      const imageMouseX = (centerX - this.translateX * this.scale) / this.scale
      const imageMouseY = (centerY - this.translateY * this.scale) / this.scale
      
      const newScale = Math.min(this.maxScale, this.scale * 1.2)
      
      this.translateX = centerX / newScale - imageMouseX
      this.translateY = centerY / newScale - imageMouseY
      this.scale = newScale
      
      this.constrainTranslation()
    },
    zoomOut() {
      const centerX = this.containerWidth / 2
      const centerY = this.containerHeight / 2
      
      const imageMouseX = (centerX - this.translateX * this.scale) / this.scale
      const imageMouseY = (centerY - this.translateY * this.scale) / this.scale
      
      const newScale = Math.max(this.minScale, this.scale / 1.2)
      
      this.translateX = centerX / newScale - imageMouseX
      this.translateY = centerY / newScale - imageMouseY
      this.scale = newScale
      
      this.constrainTranslation()
    },
    resetZoom() {
      this.fitImage()
    }
  }
}
</script>

<style scoped>
.image-viewer {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: #ddd;
}

.image-viewer img {
  display: block;
  transition: none;
}

.zoom-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 10;
}

.zoom-button {
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 16px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-button:hover {
  background: rgba(0, 0, 0, 0.7);
}

.zoom-button:active {
  background: rgba(0, 0, 0, 0.8);
}

.zoom-percentage {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  z-index: 10;
}
</style>
